/**
 * Leave Type UUIDs for reliable frontend matching
 * 
 * These UUIDs are used instead of string matching to avoid
 * issues with leave type name changes or typos.
 */

import { pluralizeDays } from '../utils/numberUtils';

export const LEAVE_TYPE_IDS = {
  // Annual Leave (multiple configurations)
  ANNUAL_LEAVE: '95d1ad31-4107-4450-8c28-dcc0892faae6',
  
  // Sick Leave types
  OUTPATIENT_SICK_LEAVE: '0f15b4ec-2a20-490d-a023-12971696e5f4',
  HOSPITALISATION_LEAVE: '6efbcce5-8bd1-444b-a5a5-ee83aa14fa2a',
  
  // Off in Lieu types (two separate types for claim vs usage)
  OFF_IN_LIEU_CLAIM: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890',  // "Claim: Off in Lieu" - for accruing OIL
  OFF_IN_LIEU: '58afc770-effc-484a-871c-42aab5194448',  // "Off in Lieu" - for using OIL balance
  
  // External Activity
  EXTERNAL_ACTIVITY: 'c6eb89ac-cc5e-4c44-9968-cd7861ae1050',
  
  // Work From Home leave type
  WORK_FROM_HOME: '1ee9d94a-d462-4e70-a719-e1abd7acfa70',
  
  // On Course (company-sanctioned training/course attendance)
  ON_COURSE: 'f9f7eeaf-07c5-4ec5-aec7-9a6774957841',
  
  // Document-required leave types (for reference)
  // These are not created automatically and require document submission
  MATERNITY_LEAVE: null, // Will be added when needed
  PATERNITY_LEAVE: null, // Will be added when needed
  CHILDCARE_LEAVE: null, // Will be added when needed
  MARRIAGE_LEAVE: null, // Will be added when needed

  // Compassionate Leave — 2 sub-types × 2 employment classifications (staff / executive_director)
  COMPASSIONATE_LEAVE_IMMEDIATE_FAMILY_STAFF: '7b53905d-5f97-4ea7-b159-73d3d5a55af0',
  COMPASSIONATE_LEAVE_IMMEDIATE_FAMILY_EXEC:  '34097dd8-749a-4b09-91d2-53bc700b712a',
  COMPASSIONATE_LEAVE_GRANDPARENT_STAFF:      'b85525ad-c8bf-48c1-8794-f517bd0a6582',
  COMPASSIONATE_LEAVE_GRANDPARENT_EXEC:       '3afaded4-6305-4a6b-880e-ce1f4f3f7d7a',
  NS_LEAVE: 'a40a7ac9-a99f-4ede-935b-db7f47ad6893', // National Service Leave
};

/**
 * OIL Business Rules Constants
 */
export const OIL_CLAIM_WINDOW_DAYS = 30;  // Must submit claim within 30 days of work date
export const OIL_EXPIRY_DAYS = 90;  // OIL expires 90 days from work date

/**
 * WFH (Work From Home) reason options
 */
export const WFH_REASONS = [
  { value: 'paperwork_documentation', label: 'Paperwork / Documentation' },
  { value: 'flexible_work_arrangement', label: 'Flexible Work Arrangement' },
  { value: 'household_matters', label: 'Household Matters' },
  { value: 'care_for_family', label: 'Care for Family / Dependents' },
  { value: 'personal_health_medical', label: 'Personal Health / Medical Reasons' },
  { value: 'bereavement_family_emergency', label: 'Bereavement / Family Emergency' },
  { value: 'external_commitments', label: 'External Commitments / Appointments' },
  { value: 'others', label: 'Others' },
];

/**
 * Helper function to check if a leave type is a sick leave type
 */
export const isSickLeaveType = (leaveTypeId) => {
  return leaveTypeId === LEAVE_TYPE_IDS.OUTPATIENT_SICK_LEAVE || 
         leaveTypeId === LEAVE_TYPE_IDS.HOSPITALISATION_LEAVE;
};

/**
 * Helper function to check if a leave type is Work From Home
 */
export const isWFHLeaveType = (leaveTypeId) => {
  return leaveTypeId === LEAVE_TYPE_IDS.WORK_FROM_HOME;
};

/**
 * Helper function to check if a leave type should NOT deduct from leave balance
 * WFH is a work arrangement change, not actual leave
 */
export const isNonDeductibleLeaveType = (leaveTypeId) => {
  return isWFHLeaveType(leaveTypeId);
};

/**
 * Helper function to check if a leave type is an annual leave type
 */
export const isAnnualLeaveType = (leaveTypeId) => {
  return leaveTypeId === LEAVE_TYPE_IDS.ANNUAL_LEAVE;
};

/**
 * Helper function to check if a leave type is Off in Lieu (usage - for taking time off)
 */
export const isOffInLieuType = (leaveTypeId) => {
  return leaveTypeId === LEAVE_TYPE_IDS.OFF_IN_LIEU;
};

/**
 * Helper function to check if a leave type is Claim: Off in Lieu (accrual - for claiming OIL)
 */
export const isOffInLieuClaimType = (leaveTypeId) => {
  return leaveTypeId === LEAVE_TYPE_IDS.OFF_IN_LIEU_CLAIM;
};

/**
 * Helper function to check if a leave type is any OIL type (claim or usage)
 */
export const isAnyOffInLieuType = (leaveTypeId) => {
  return leaveTypeId === LEAVE_TYPE_IDS.OFF_IN_LIEU || 
         leaveTypeId === LEAVE_TYPE_IDS.OFF_IN_LIEU_CLAIM;
};

/**
 * Helper function to check if a leave type is External Activity
 */
export const isExternalActivityType = (leaveTypeId) => {
  return leaveTypeId === LEAVE_TYPE_IDS.EXTERNAL_ACTIVITY;
};

/**
 * Helper function to check if a leave type is On Course
 */
export const isOnCourseType = (leaveTypeId) => {
  return leaveTypeId === LEAVE_TYPE_IDS.ON_COURSE;
};

/**
 * Work-status leave types: zero-balance-impact operational status markers
 * that HR/RO can create on behalf of employees via exception resolution.
 * Balance-consuming leave (AL, SL, etc.) must be employee-initiated.
 */
export const WORK_STATUS_LEAVE_IDS = new Set([
  LEAVE_TYPE_IDS.WORK_FROM_HOME,
  LEAVE_TYPE_IDS.EXTERNAL_ACTIVITY,
  LEAVE_TYPE_IDS.ON_COURSE,
]);

export const isWorkStatusLeaveType = (leaveTypeId) => {
  return WORK_STATUS_LEAVE_IDS.has(leaveTypeId);
};

const WORK_STATUS_NAMES = ['work from home', 'external activity', 'external meeting', 'on course'];

export const isWorkStatusLeaveTypeName = (name = '') => {
  const n = (name || '').toLowerCase();
  return WORK_STATUS_NAMES.some(ws => n.includes(ws));
};

/**
 * Helper function to check if a leave type is On Course by name
 */
export const isOnCourseName = (name = '') => {
  const n = (name || '').toLowerCase();
  return n === 'on course';
};

/**
 * Helper function to check if a leave type is Off in Lieu by name (any type)
 */
export const isOffInLieuName = (name = '') => {
  const n = (name || '').toLowerCase();
  return n.includes('off in lieu') || n.includes('compensatory') || n.includes('saturday work');
};

/**
 * Helper function to check if a leave type is "Claim: Off in Lieu" by name
 */
export const isOffInLieuClaimName = (name = '') => {
  const n = (name || '').toLowerCase();
  return n.includes('claim') && n.includes('off in lieu');
};

/**
 * Helper function to check if a leave type is "Off in Lieu" usage by name (not claim)
 */
export const isOffInLieuUsageName = (name = '') => {
  const n = (name || '').toLowerCase();
  // Is "Off in Lieu" but NOT "Claim: Off in Lieu"
  return n.includes('off in lieu') && !n.includes('claim');
};

/**
 * Helper function to check if a leave type is External Activity by name
 */
export const isExternalActivityName = (name = '') => {
  const n = (name || '').toLowerCase();
  // Removed broad 'training' match to prevent false positives with "On Course" or future "Training Leave" types
  return n.includes('external activity') || n.includes('external meeting');
};

/**
 * Helper function to check if a leave type is NS Leave
 */
export const isNSLeaveType = (leaveTypeId) => {
  return leaveTypeId === LEAVE_TYPE_IDS.NS_LEAVE;
};

/**
 * Helper function to check if a leave type is NS Leave by name
 */
export const isNSLeaveName = (name = '') => {
  const n = (name || '').toLowerCase();
  return n.includes('national service') || n.includes('ns leave') || n === 'ns';
};

/**
 * Helper function to check if a leave type is Compassionate Leave by UUID
 * Covers all 4 variants (2 sub-types × 2 employment classifications)
 */
export const isCompassionateLeaveType = (leaveTypeId) => {
  return (
    leaveTypeId === LEAVE_TYPE_IDS.COMPASSIONATE_LEAVE_IMMEDIATE_FAMILY_STAFF ||
    leaveTypeId === LEAVE_TYPE_IDS.COMPASSIONATE_LEAVE_IMMEDIATE_FAMILY_EXEC  ||
    leaveTypeId === LEAVE_TYPE_IDS.COMPASSIONATE_LEAVE_GRANDPARENT_STAFF      ||
    leaveTypeId === LEAVE_TYPE_IDS.COMPASSIONATE_LEAVE_GRANDPARENT_EXEC
  );
};

/**
 * Helper function to check if a leave type is Compassionate Leave by name
 * Use isCompassionateLeaveType() when you have the UUID — this is a name-based fallback.
 */
export const isCompassionateLeaveName = (name = '') => {
  const n = (name || '').toLowerCase();
  return n.includes('compassionate') || n.includes('bereavement');
};

/**
 * Compassionate Leave relationship options
 */
export const COMPASSIONATE_RELATIONSHIPS = {
  IMMEDIATE_FAMILY: [
    { value: 'spouse', label: 'Spouse' },
    { value: 'child', label: 'Child' },
    { value: 'parent', label: 'Parent' },
    { value: 'sibling', label: 'Sibling' },
  ],
  GRANDPARENT: [
    { value: 'paternal_grandfather', label: 'Paternal Grandfather' },
    { value: 'paternal_grandmother', label: 'Paternal Grandmother' },
    { value: 'maternal_grandfather', label: 'Maternal Grandfather' },
    { value: 'maternal_grandmother', label: 'Maternal Grandmother' },
  ],
};

/**
 * Get the entitlement days based on relationship category
 */
export const getCompassionateEntitlementDays = (relationship) => {
  const immediateFamilyValues = COMPASSIONATE_RELATIONSHIPS.IMMEDIATE_FAMILY.map(r => r.value);
  if (immediateFamilyValues.includes(relationship)) return 3;
  
  const grandparentValues = COMPASSIONATE_RELATIONSHIPS.GRANDPARENT.map(r => r.value);
  if (grandparentValues.includes(relationship)) return 1;
  
  return 0;
};

/**
 * Get all compassionate relationship options as a flat array (for select dropdowns).
 * If leaveTypeName is provided, filter to only the relevant sub-type.
 */
export const getAllCompassionateRelationships = (leaveTypeName = '') => {
  const n = (leaveTypeName || '').toLowerCase();
  if (n.includes('grandparent')) {
    return COMPASSIONATE_RELATIONSHIPS.GRANDPARENT;
  }
  if (n.includes('immediate family')) {
    return COMPASSIONATE_RELATIONSHIPS.IMMEDIATE_FAMILY;
  }
  // Fallback: show all with group headers
  return [
    { label: '── Immediate Family (3 days) ──', value: '', disabled: true },
    ...COMPASSIONATE_RELATIONSHIPS.IMMEDIATE_FAMILY,
    { label: '── Grandparent (1 day) ──', value: '', disabled: true },
    ...COMPASSIONATE_RELATIONSHIPS.GRANDPARENT,
  ];
};

/**
 * Dashboard compassionate leave suppression rule.
 *
 * Hidden when: never used (pre-bereavement — don't advertise as a perk)
 *           OR fully exhausted (post-bereavement — don't leave a grief marker on the dashboard)
 * Visible when: partially used with remaining days > 0 (employee needs to track what's left)
 */
export const shouldHideDashboardCompassionateCard = (balance) => {
  if (!isCompassionateLeaveType(balance.leave_type?.id)) return false;
  const usedDays     = Number(balance.used_days ?? 0);
  const remainingDays = Number(balance.remaining_days ?? balance.remaining_balance ?? 0);
  const entitledDays  = Number(balance.entitled_days ?? balance.total_entitlement ?? 0);
  const neverUsed    = usedDays === 0 && remainingDays === entitledDays;
  const fullyUsed    = remainingDays <= 0;
  return neverUsed || fullyUsed;
};

/**
 * Leave page compassionate leave suppression rule.
 *
 * Hidden when: never used (same pre-bereavement rule as Dashboard)
 * Visible when: any usage — including exhausted, so the employee can see their full allocation history
 */
export const shouldHideLeavePageCompassionateCard = (balance) => {
  if (!isCompassionateLeaveType(balance.leave_type?.id)) return false;
  const usedDays      = Number(balance.used_days ?? 0);
  const remainingDays = Number(balance.remaining_days ?? balance.remaining_balance ?? 0);
  const entitledDays  = Number(balance.entitled_days ?? balance.total_entitlement ?? 0);
  return usedDays === 0 && remainingDays === entitledDays;
};

/**
 * Helper function to get the display name for a leave type ID
 */
export const getLeaveTypeDisplayName = (leaveTypeId) => {
  switch (leaveTypeId) {
    case LEAVE_TYPE_IDS.ANNUAL_LEAVE:
      return 'Annual Leave';
    case LEAVE_TYPE_IDS.OUTPATIENT_SICK_LEAVE:
      return 'Outpatient Sick Leave';
    case LEAVE_TYPE_IDS.HOSPITALISATION_LEAVE:
      return 'Hospitalisation Leave';
    case LEAVE_TYPE_IDS.OFF_IN_LIEU_CLAIM:
      return 'Claim: Off in Lieu';
    case LEAVE_TYPE_IDS.OFF_IN_LIEU:
      return 'Off in Lieu';
    case LEAVE_TYPE_IDS.EXTERNAL_ACTIVITY:
      return 'External Activity';
    case LEAVE_TYPE_IDS.ON_COURSE:
      return 'On Course';
    default:
      return 'Unknown Leave Type';
  }
};

/**
 * Helper function to calculate OIL expiry date from work date
 * @param {Date|string} workDate - The date work was performed
 * @returns {Date} - The expiry date (90 days from work date)
 */
export const calculateOILExpiryDate = (workDate) => {
  const date = workDate instanceof Date ? workDate : new Date(workDate);
  const expiry = new Date(date);
  expiry.setDate(expiry.getDate() + OIL_EXPIRY_DAYS);
  return expiry;
};

/**
 * Helper function to check if a work date is within the 30-day claim window
 * Work must be completed (yesterday at the latest, not today)
 * @param {Date|string} workDate - The date work was performed
 * @returns {object} - { isValid: boolean, daysAgo: number, message: string }
 */
export const validateOILClaimWindow = (workDate) => {
  const date = workDate instanceof Date ? workDate : new Date(workDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);
  
  const diffTime = today - date;
  const daysAgo = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  
  // Can't claim for today - work day hasn't ended yet
  if (daysAgo === 0) {
    return {
      isValid: false,
      daysAgo: daysAgo,
      message: 'Cannot claim for today - work day must be completed first'
    };
  }
  
  // Can't claim for future dates
  if (daysAgo < 0) {
    return {
      isValid: false,
      daysAgo: daysAgo,
      message: 'Work date cannot be in the future'
    };
  }
  
  // Can't claim for dates older than 30 days
  if (daysAgo > OIL_CLAIM_WINDOW_DAYS) {
    return {
      isValid: false,
      daysAgo: daysAgo,
      message: `Work date is more than ${OIL_CLAIM_WINDOW_DAYS} days ago. OIL claims must be submitted within ${OIL_CLAIM_WINDOW_DAYS} days.`
    };
  }
  
  const daysRemaining = OIL_CLAIM_WINDOW_DAYS - daysAgo;
  return {
    isValid: true,
    daysAgo: daysAgo,
    daysRemaining: daysRemaining,
    message: daysRemaining <= 7 
      ? `⚠️ Only ${daysRemaining} ${pluralizeDays(daysRemaining)} left to submit this claim`
      : `✓ Within claim window (${daysRemaining} days remaining)`
  };
}; 